import React from "react";
import { endpoints } from "@/lib/api";

/**
 * AuthorAvatar — shows an uploaded profile photo when present, otherwise
 * falls back to the author's first initial on a sand-tone chip.
 *
 * Props:
 *  - name:     author display name (used for initial fallback + alt text)
 *  - src:      storage path (as returned from /api/upload). Optional.
 *  - size:     "sm" (32px), "md" (36px), "lg" (48px). Default "md".
 *  - ring:     "moss" | "clay" | null — a hairline colored ring, optional
 *  - testid:   custom data-testid
 */
export default function AuthorAvatar({ name = "", src, size = "md", ring = null, testid }) {
  const dim =
    size === "sm" ? "w-8 h-8 text-sm" :
    size === "lg" ? "w-12 h-12 text-lg" :
    "w-9 h-9 text-base";
  const initial = (name || "•").trim().charAt(0).toUpperCase();
  const ringCls =
    ring === "moss" ? "ring-2 ring-[hsl(var(--moss))]/40" :
    ring === "clay" ? "ring-2 ring-[hsl(var(--clay))]/40" :
    "";

  if (src) {
    const url = /^https?:\/\//i.test(src) ? src : endpoints.fileUrl(src);
    return (
      <img
        src={url}
        alt={name || "member"}
        data-testid={testid || `avatar-${name}`}
        loading="lazy"
        className={`${dim} rounded-full object-cover bg-[hsl(var(--sand))] shrink-0 ${ringCls}`}
      />
    );
  }

  return (
    <div
      data-testid={testid || `avatar-${name}`}
      aria-label={name || "member"}
      className={`${dim} rounded-full bg-[hsl(var(--sand))] grid place-items-center font-display shrink-0 ${ringCls}`}
    >
      {initial}
    </div>
  );
}
